import { createStubImplementation } from '@ardoq/stub-implementation';
import {
  APIFieldAttributes,
  APIReferenceAttributes,
  APIReferenceType,
  ArdoqId,
  ArrowType,
  FEParentChildReferenceAttributes,
} from '@ardoq/api-types';
import {
  getEmptyGraphModel,
  GetCssClassNamesOption,
  GlobalReferenceType,
  GlobalReferenceTypeId,
  GraphModelShape,
  StubReferenceWithTypeName,
  type FieldValueScopeSource,
} from '@ardoq/data-model';
import { ReferenceFilterContext } from '@ardoq/filter-helpers';
import { returnZero } from '@ardoq/common-helpers';

export type IsExternallyManaged = (referenceId: ArdoqId) => boolean;

export type GetGlobalTypeId = (
  referenceId: ArdoqId
) => GlobalReferenceTypeId | null;

export type IsIncludedInContextByFilter = (
  reference: APIReferenceAttributes,
  ctx?: ReferenceFilterContext,
  scopeSource?: FieldValueScopeSource | null
) => boolean;

export type GetRawLabel = (referenceId: ArdoqId) => string | null;

export type IsReference = (referenceId: ArdoqId) => boolean;

export type HasURLFields = (referenceId: ArdoqId) => boolean;

type GetReferenceCssColors = (
  referenceId: ArdoqId,
  options: GetCssClassNamesOption
) => { fill?: string; stroke?: string } | null;

export type GetCssClassNames = (referenceId: ArdoqId) => string | null;
export type GetCssColors = (
  referenceId: ArdoqId,
  options: GetCssClassNamesOption
) => { fill?: string; stroke?: string } | null;

export type GetLineBeginning = (
  referenceId: ArdoqId,
  isAggregated?: boolean
) => ArrowType;

export type GetLineEnding = (
  referenceId: ArdoqId,
  isAggregated?: boolean
) => ArrowType;

export type GetStyle = (referenceId: ArdoqId) => string | null;

export type GetReferencesForComponent = (
  componentId: ArdoqId
) => APIReferenceAttributes[];

export type GetReferencesInWorkspace = (
  workspaceId: ArdoqId
) => APIReferenceAttributes[];

export type GetReferencesInRootWorkspace = (
  workspaceId: ArdoqId
) => APIReferenceAttributes[];

export type GetReferenceTypeName = (
  reference: APIReferenceAttributes
) => string | null;

export type GetReferenceTypeNameByReferenceId = (
  referenceId: string
) => string | null;

export type GetFields = (referenceId: ArdoqId) => APIFieldAttributes[];

export type GetModelType = (referenceId: ArdoqId) => APIReferenceType | null;

export type Get = (referenceId: ArdoqId) => APIReferenceAttributes | null;

export type GetGlobalReferenceType = (
  referenceId: ArdoqId
) => GlobalReferenceType | null;

/**
 * To get a filter function that can be used to filter references or components
 * by type name.
 * @param table - A table to resolve workspace ids to type ids for a give type
 * name.
 * @param virtualTypeId - The id of the virtual type for the given name.
 * @returns A filter function that can be used to filter references or components
 * by type name taking the id of the entity as argument.
 */
export type GetTypeNameFilter = (
  table: Record<string, string | number | undefined>,
  virtualTypeId: string | number | undefined
) => (id: ArdoqId) => boolean;

type GetWorkspaceReferences = (id: ArdoqId) => APIReferenceAttributes[];
export type GetAllReferences = () => APIReferenceAttributes[];

export type GetReferencesCount = () => number;
export type GetStubReferencesWithTypeName = () => StubReferenceWithTypeName[];
/**
 * Gets a GraphModelShape with an empty referenceTypes list.
 */
export type GetGraphSnapshot = (
  scopeReferenceIds: ArdoqId[],
  parentChildReferences: FEParentChildReferenceAttributes[]
) => GraphModelShape;

export type GetMatchingColorFilterIds = (referenceId: ArdoqId) => string[];

export type ReferenceInterfaceImplementation = {
  isExternallyManaged: IsExternallyManaged;
  getGlobalTypeId: GetGlobalTypeId;
  isIncludedInContextByFilter: IsIncludedInContextByFilter;
  isReference: IsReference;
  hasURLFields: HasURLFields;
  getReferenceCssColors: GetReferenceCssColors;
  getCssClassNames: GetCssClassNames;
  getCssColors: GetCssColors;
  getLineBeginning: GetLineBeginning;
  getLineEnding: GetLineEnding;
  getStyle: GetStyle;
  getReferencesForComponent: GetReferencesForComponent;
  getReferencesInWorkspace: GetReferencesInWorkspace;
  getReferencesInRootWorkspace: GetReferencesInRootWorkspace;
  getReferenceTypeNameByReferenceId: GetReferenceTypeNameByReferenceId;
  getFields: GetFields;
  getModelType: GetModelType;
  get: Get;
  getGlobalReferenceType: GetGlobalReferenceType;
  getTypeNameFilter: GetTypeNameFilter;
  getWorkspaceReferences: GetWorkspaceReferences;
  getAllReferences: GetAllReferences;
  getDisplayLabel: GetRawLabel;
  getReferencesCount: GetReferencesCount;
  getStubReferencesWithTypeName: GetStubReferencesWithTypeName;
  getGraphSnapshot: GetGraphSnapshot;
  getMatchingColorFilterIds: GetMatchingColorFilterIds;
};

const defaultImplementation: ReferenceInterfaceImplementation = {
  isExternallyManaged: () => false,
  getGlobalTypeId: () => null,
  isIncludedInContextByFilter: () => true,
  isReference: () => false,
  hasURLFields: () => false,
  getReferenceCssColors: () => null,
  getCssClassNames: () => null,
  getCssColors: () => null,
  getLineBeginning: () => ArrowType.NONE,
  getLineEnding: () => ArrowType.NONE,
  getStyle: () => null,
  getReferencesForComponent: () => [],
  getReferencesInWorkspace: () => [],
  getReferencesInRootWorkspace: () => [],
  getReferenceTypeNameByReferenceId: () => null,
  getFields: () => [],
  getModelType: () => null,
  get: () => null,
  getGlobalReferenceType: () => null,
  getTypeNameFilter: () => () => false,
  getWorkspaceReferences: () => [],
  getAllReferences: () => [],
  getDisplayLabel: () => null,
  getReferencesCount: returnZero,
  getStubReferencesWithTypeName: () => [],
  getGraphSnapshot: () => getEmptyGraphModel(),
  getMatchingColorFilterIds: () => [],
};

const {
  isExternallyManaged,
  getGlobalTypeId,
  isIncludedInContextByFilter,
  isReference,
  hasURLFields,
  getReferenceCssColors,
  getCssClassNames,
  getCssColors,
  getLineBeginning,
  getLineEnding,
  getStyle,
  getReferencesForComponent,
  getReferencesInWorkspace,
  getReferencesInRootWorkspace,
  getReferenceTypeNameByReferenceId,
  getFields,
  getModelType,
  get,
  getGlobalReferenceType,
  registerImplementation,
  getTypeNameFilter,
  getWorkspaceReferences,
  getAllReferences,
  getDisplayLabel,
  getReferencesCount,
  getStubReferencesWithTypeName,
  getGraphSnapshot,
  getMatchingColorFilterIds,
} = createStubImplementation(
  '@ardoq/reference-interface',
  defaultImplementation
);

/**
 * `referenceInterface` is a Backbone-era singleton: every member resolves the
 * reference, and often its model type and the active filters, from global mutable
 * state at call time.
 *
 * The app is stream-based now. Resolve references from `references$`
 * (`streams/references/references$.ts`) at a permitted boundary -- a `*$.ts`
 * file, a file inside a `*$/` folder, or a `*routines.ts` -- and thread the
 * resolved data downstream as plain parameters.
 *
 * Reference *type* resolution has pure counterparts in `modelOperations`
 * (`@ardoq/core`) -- `getRefType`, `getRefTypeById`, `getRefTypeName`,
 * `getModelType`, `getStyle`, `getLineBeginning`, `getLineEnding`. Label
 * formatting lives in `referenceLabelOperations`
 * (`@ardoq/reference-operations`), attribute shaping in
 * `referenceAttributesOperations` (`@ardoq/core`), and the `EnhancedScopeData`
 * flavour -- including documented pure replacements for `getCssClassNames` and
 * `getCssColors` -- in `referenceUtils` (`@ardoq/scope-data`).
 *
 * Every member below is deprecated. See
 * `packages/eslint-plugin-ardoq-lint/docs/rules/no-model-interface-call.md`
 * for the hoisting recipe and the interface-to-stream substitution table.
 */
export const referenceInterface = {
  defaultImplementation,
  /**
   * @deprecated Read `references$.byId[id]` and inspect its own `ardoq-persistent` managed payload against the workspace integrations from `workspaceUtils.getWorkspaceIntegrations` (`@ardoq/scope-data`).
   */
  isExternallyManaged,
  /**
   * @deprecated Call `referenceUtils.toGlobalReferenceTypeId` or `referenceUtils.getGlobalTypeId` (`@ardoq/scope-data`).
   */
  getGlobalTypeId,
  /**
   * @deprecated Join `includedInContext$` (`streams/filters/includedInContext$.ts`), or call the pure `isReferenceIdIncludedInContextByFilter` from `@ardoq/reference-interface` with resolved filters.
   */
  isIncludedInContextByFilter,
  /**
   * @deprecated Read `references$.byId[id]` for presence, or call `referenceUtils.isReference` (`@ardoq/scope-data`).
   */
  isReference,
  /**
   * @deprecated Call `fieldOperations.isURLField` (`@ardoq/core`) over fields from `fields$`.
   */
  hasURLFields,
  /**
   * @deprecated Call `referenceUtils.getReferenceDisplayColorAsSVGAttributes(scopeData, filterColorData, referenceId, options)` (`@ardoq/scope-data`).
   */
  getReferenceCssColors,
  /**
   * @deprecated Call `referenceUtils.getReferenceCSSClassNames(scopeData, filterColorData, referenceId)` (`@ardoq/scope-data`) -- documented as the pure replacement for this member.
   */
  getCssClassNames,
  /**
   * @deprecated Call `referenceUtils.getReferenceDisplayColorAsSVGAttributes(scopeData, filterColorData, referenceId, options)` (`@ardoq/scope-data`) -- documented as the pure replacement for this member.
   */
  getCssColors,
  /**
   * @deprecated Call `modelOperations.getLineBeginning(referenceType)` (`@ardoq/core`).
   */
  getLineBeginning,
  /**
   * @deprecated Call `modelOperations.getLineEnding(referenceType)` (`@ardoq/core`).
   */
  getLineEnding,
  /**
   * @deprecated Call `modelOperations.getStyle(referenceType)` (`@ardoq/core`).
   */
  getStyle,
  /**
   * @deprecated Read `references$.models` and filter on `source` / `target`, or join `graphModel$` for the adjacency.
   */
  getReferencesForComponent,
  /**
   * @deprecated Read `references$.models` and filter on `rootWorkspace`; `entityOperations.toIds` (`@ardoq/core`) if you only need the ids.
   */
  getReferencesInWorkspace,
  /**
   * @deprecated Read `references$.models` and filter on `rootWorkspace`; `entityOperations.toIds` (`@ardoq/core`) if you only need the ids.
   */
  getReferencesInRootWorkspace,
  /**
   * @deprecated Call `enhancedScopeDataOperations.getReferenceTypeByReferenceId` (`@ardoq/scope-data`), or `modelOperations.getRefTypeName(referenceTypes, typeId)` (`@ardoq/core`).
   */
  getReferenceTypeNameByReferenceId,
  /**
   * @deprecated Call `enhancedScopeDataOperations.getReferenceFields` (`@ardoq/scope-data`), or select from `fields$` with `fieldOperations` (`@ardoq/core`).
   */
  getFields,
  /**
   * @deprecated Call `modelOperations.getModelType(referenceTypes, typeId)` (`@ardoq/core`) with types from `models$`.
   */
  getModelType,
  /**
   * @deprecated Read `references$.byId[id]`.
   */
  get,
  /**
   * @deprecated Call `modelOperations.getRefType(referenceTypes, typeId)` (`@ardoq/core`) with types from `models$`.
   */
  getGlobalReferenceType,
  registerImplementation,
  /**
   * @deprecated Call `modelOperations.getRefTypeName` (`@ardoq/core`) over types from `models$` and build the predicate from the result.
   */
  getTypeNameFilter,
  /**
   * @deprecated Read `references$.models` and filter on `rootWorkspace`; `entityOperations.toIds` (`@ardoq/core`) if you only need the ids.
   */
  getWorkspaceReferences,
  /**
   * @deprecated Read `references$.models`.
   */
  getAllReferences,
  /**
   * @deprecated Call `referenceLabelOperations` (`@ardoq/reference-operations`) with the label rules from `formattingAndLabelRules$` and fields from `fields$`.
   */
  getDisplayLabel,
  /**
   * @deprecated Read `references$.models.length`.
   */
  getReferencesCount,
  /**
   * @deprecated Join `references$` and `models$` and shape the stubs with `modelOperations.getRefTypeName` (`@ardoq/core`).
   */
  getStubReferencesWithTypeName,
  /**
   * @deprecated Join `graphModel$` (`modelInterface/graphModel$.ts`), or call `enhancedScopeDataOperations.getGraphSnapshot` (`@ardoq/scope-data`).
   */
  getGraphSnapshot,
  /**
   * @deprecated Call `filterColorDataOperations.getReferenceFilterColor` (`@ardoq/scope-data`) with the conditional formatting resolved from `streams/filters/`.
   */
  getMatchingColorFilterIds,
};
